import { Link, useLocation, useNavigate } from "react-router-dom"
import { Button, Paper, Typography, UserTextRenderer } from "matsuri-ui"
import { css } from "@emotion/react"
import dayjs from "dayjs"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { theme } from "../../theme"
import { InfoFooter } from "./parts/InfoFooter"
import { OperationAttachmentImages } from "./parts/OperationAttachmentImages"
import { OperationInfoProvider, useOperationInfoCtx } from "./providers/context"
import {
    OperationPhotoTourProvider,
    useOperationPhotoTourContext
} from "./providers/photoTour"
import { PlacementAddress } from "./parts/PlacementAddress"
import { RoomKeyDetails } from "@/templates/RoomKeyDetails"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { InfoSection } from "@/parts/OperationInfo/InfoSection"
import { ListingOccupancyInfo } from "@/parts/OperationInfo/ListingOccupancyInfo"
import { RoomWifiInfo } from "@/parts/OperationInfo/RoomWifiInfo/RoomWifiInfo"
import { RoomGarbageInfo } from "@/parts/OperationInfo/RoomGarbageInfo/RoomGarbageInfo"
import { EmbeddedGoogleMap } from "@/components/EmbeddedGoogleMap"
import { openUrlInNewTab, sumycaListingUrl } from "@/utils/urls"
import { useCleaningExtraInfo } from "@/hooks/useCleaning"
import { useRoomKeyInformation } from "@/hooks/useListingBigQueryTable"
import { useTranslation } from "@/hooks/useTranslation"

const OperationDetail = () => {
    const { t } = useTranslation()
    const { token } = useAuth()
    const { tour, items } = useOperationPhotoTourContext()
    const { operationId, operation } = useOperationInfoCtx()
    const { data: cleaningExtraInfo } = useCleaningExtraInfo(token, {
        cleaningId: operationId
    })

    const scheduleText =
        operation?.scheduleStart && operation?.scheduleEnd
            ? `${dayjs.unix(operation?.scheduleStart).format("HH:mm")} - ${dayjs.unix(operation?.scheduleEnd).format("HH:mm")}`
            : undefined

    const navigate = useNavigate()
    const location = useLocation()

    const googleMapVariant = operation?.address
        ? ({
              type: "place",
              query: operation.address
          } as const)
        : undefined

    const roomKeyInformation = useRoomKeyInformation(token, operationId)

    // 物件のツアーのときだけ鍵番号の API に ID を渡す（共用部には物件の鍵情報が無い）
    const listingOperationId =
        operation?.placement === "listing" ? operation?.id : undefined

    return (
        <>
            <Paper
                square
                style={{
                    backgroundColor:
                        theme.palette.intentions.cleanerBackground.main,
                    paddingTop: "env(safe-area-inset-top, 0px)"
                }}
            >
                <MobileAppHeader
                    title={
                        <Typography as="h4" clamp={1} align="center">
                            {operation?.placement === "listing"
                                ? operation?.listingName
                                : operation?.commonAreaName}
                        </Typography>
                    }
                    onPrev={() =>
                        // 画面遷移がうまくいかないため、条件分岐を追加
                        location.pathname.endsWith("/info")
                            ? navigate("/")
                            : navigate(-1)
                    }
                />
            </Paper>

            <div
                css={css`
                    display: grid;
                    gap: 40px;
                    max-width: 100%;
                    margin: 20px;
                    margin-bottom: 250px; //footer部分に被らない高さ
                `}
            >
                <InfoSection>
                    <Typography variant="h4" bolder>
                        {t("物件情報")}
                    </Typography>

                    {operation?.listingName && (
                        <div>
                            <Typography bolder>{t("物件名")}</Typography>
                            <Typography variant="body">
                                <UserTextRenderer>
                                    {operation?.listingName}
                                </UserTextRenderer>
                            </Typography>
                        </div>
                    )}
                    {operation?.commonAreaName && (
                        <div>
                            <Typography bolder>{t("共用部名")}</Typography>
                            <Typography variant="body">
                                <UserTextRenderer>
                                    {operation?.commonAreaName}
                                </UserTextRenderer>
                            </Typography>
                        </div>
                    )}
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 8px;
                        `}
                    >
                        <Typography bolder>
                            {t("物件の詳細情報（Sumycaリンク）")}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            readOnly={!cleaningExtraInfo?.sumycaListingId}
                            onClick={() => {
                                if (cleaningExtraInfo?.sumycaListingId) {
                                    openUrlInNewTab(
                                        sumycaListingUrl(
                                            cleaningExtraInfo.sumycaListingId
                                        )
                                    )
                                }
                            }}
                            endIcon={<OpenInNewIcon />}
                        >
                            {t("Sumycaで確認する")}
                        </Button>
                        <Typography>{t("※外部リンクで開きます")}</Typography>
                    </div>
                    {operation?.placement ? (
                        <PlacementAddress
                            placementType={operation?.placement}
                            operationId={operationId}
                        />
                    ) : null}
                    {googleMapVariant && (
                        <EmbeddedGoogleMap variant={googleMapVariant} />
                    )}
                </InfoSection>

                <ListingOccupancyInfo operationId={operationId} />

                <InfoSection>
                    <Typography variant="h4" bolder>
                        {t("ツアー情報")}
                    </Typography>
                    <div>
                        <Typography bolder>{t("作業日")}</Typography>
                        <Typography>
                            {operation?.cleaningDate} {scheduleText}
                        </Typography>
                    </div>
                    {operation?.deadlineAt && (
                        <div>
                            <Typography
                                bolder
                                style={{
                                    color: "red"
                                }}
                            >
                                {t("清掃期限")}
                            </Typography>
                            <Typography
                                style={{
                                    color: "red",
                                    fontWeight: 700
                                }}
                            >
                                {`〜${dayjs.unix(operation.deadlineAt).format("HH:mm")}`}
                            </Typography>
                        </div>
                    )}
                    <div>
                        <Typography bolder>{t("ステータス")}</Typography>
                        <Typography>{operation?.status}</Typography>
                    </div>
                    <div>
                        <Typography bolder>{t("メモ")}</Typography>
                        <Typography>
                            <UserTextRenderer>
                                {operation?.note || "-"}
                            </UserTextRenderer>
                        </Typography>
                    </div>
                    {operation?.cleaningCompanyId && (
                        <div>
                            <Typography bolder>
                                {t("担当業者用オペレーションメモ")}
                            </Typography>
                            <Typography>
                                <UserTextRenderer>
                                    {operation?.cleaningCompanyMemo || "-"}
                                </UserTextRenderer>
                            </Typography>
                        </div>
                    )}
                    <div>
                        <Typography bolder>{t("画像指示")}</Typography>
                        <OperationAttachmentImages operationId={operationId} />
                    </div>
                    <div>
                        <Typography bolder>{t("ツアー名")}</Typography>
                        <Typography variant="body">
                            <UserTextRenderer>{tour?.title}</UserTextRenderer>
                        </Typography>
                    </div>
                    <div>
                        <Typography bolder>{t("ツアー説明")}</Typography>
                        <Typography variant="body">
                            <UserTextRenderer>
                                {tour?.description}
                            </UserTextRenderer>
                        </Typography>
                    </div>

                    <div
                        css={css`
                            display: grid;
                            gap: 8px;
                        `}
                    >
                        <Typography bolder>{t("撮影箇所の一覧")}</Typography>
                        <ul
                            css={css`
                                display: grid;
                                gap: 8px;
                                margin-left: 32px;
                            `}
                        >
                            {operation?.status !== "finished" &&
                                items?.map(item => (
                                    <li key={item.id}>
                                        <UserTextRenderer>
                                            {item.title}
                                        </UserTextRenderer>
                                    </li>
                                ))}
                            {operation?.status === "finished" &&
                                items?.map((item, index) => (
                                    <li key={item.id}>
                                        <Link
                                            to={`/tour/${operationId}/photo/item/${index}`}
                                            css={css`
                                                display: flex;
                                                align-items: center;
                                                justify-content: space-between;
                                            `}
                                        >
                                            <Typography variant="body">
                                                {item.title}
                                            </Typography>
                                        </Link>
                                    </li>
                                ))}
                        </ul>
                    </div>
                </InfoSection>
                <RoomKeyDetails
                    operationId={listingOperationId}
                    listingId={operation?.listingId}
                    cleaningDateString={operation?.cleaningDate}
                    roomKeyInformation={roomKeyInformation}
                    // ツアーも m2m-cleaning では清掃と同じ ID で扱われるため、同じ API でゲストキー番号を取得できる
                    guestKeyboxCleaningId={listingOperationId}
                />
                <RoomWifiInfo operationId={operationId} />
                <RoomGarbageInfo operationId={operationId} />
            </div>
            <InfoFooter />
        </>
    )
}
export const OperationInfoPage = () => {
    return (
        <OperationInfoProvider>
            <OperationPhotoTourProvider>
                <OperationDetail />
            </OperationPhotoTourProvider>
        </OperationInfoProvider>
    )
}
